import { createTheme } from '@mui/material';
import './fonts.css';

const customDarkTheme = createTheme({
    spacing: 8,
    breakpoints: {
        keys: ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'],
        values: {
            xs: 360,
            sm: 576,
            md: 768,
            lg: 1440,
            xl: 1512,
            xxl: 1728,
            xxxl: 1920,
        },
    },
    typography: {
        /* set the default font-family, weight and line-height which overwrites the mui font-family */
        fontFamily: 'DIN OT, sans-serif',
        fontWeightLight: 100,
        fontWeightRegular: 400,
        fontWeightBold: 700,
        lineHeight: '1.313rem',
        size: {
            xs: '0.875rem',
            s: '0.95rem',
            m: '16px',
            l: '1.5rem',
            xl: '1.5rem',
        },
        h1: {
            fontFamily: 'DIN OT, sans-serif',
            fontWeight: 900,
            fontSize: '4.5938rem',
        },
        h2: {
            fontFamily: 'DIN OT, sans-serif',
            fontWeight: 700,
            lineHeight: '3.6563rem',
        },
        h3: {
            fontFamily: 'DIN OT, sans-serif',
            fontWeight: 700,
            fontSize: '2.9063rem',
        },
        h4: {
            fontFamily: 'DIN OT, sans-serif',
            fontWeight: 400,
            fontSize: '2.3438rem',
        },
        h5: {
            fontFamily: 'DIN OT, sans-serif',
            fontWeight: 400,
            fontSize: '1.875rem',
        },
    },
    dimensions: {
        tableRowHeight: '29.25px',
        inputHeightSmall: '25px',
        inputHeight: '31.25px',
        letterSpacing: '0.04em',
        borderRadiusInput: '4px',
        borderRadiusPanel: '4px',
        borderRadiusListItem: '8px',
        moduleSpacing: '7px',
        headerSpacing: '5px',
        headerSpacingSmall: '3px',
        shadowPanel:
            '0px 2px 1px -1px rgba(28, 50, 87, 0.3), 0px 1px 1px 0px rgba(28, 50, 87, 0.24), 0px 1px 3px 0px rgba(28, 50, 87, 0.22)',
        shadowPanelHover:
            '0px 3px 1px -2px rgba(28, 50, 87,0.3),0px 2px 2px 0px rgba(28, 50, 87, 0,0.24),0px 1px 5px 0px rgba(28, 50, 87,0.22)',
        shadowDialog: '20px 20px 60px rgba(28, 50, 87,0.1), -20px -20px 60px rgba(28, 50, 87,0.1)',
        shadowButton: '0px 2px 1px -1px rgba(0,0,0, 0.2), 0px 1px 1px 0px rgba(0,0,0, 0.14), 0px 1px 3px 0px rgba(0,0,0, 0.12)',
        textShadow: '1px 1px 1px rgba(0,0,0, 0.3)',
        neomorphShadow: '20px 20px 60px rgba(0,0,0,0.2), -20px -20px 60px rgba(0,0,0,0.0)',
        shadowGlobalNavigation:
            '0px 2px 1px 1px rgba(24, 39, 68, 0.3), 0px 1px 1px 0px rgba(24, 39, 68, 0.24), 0px 1px 3px 0px rgba(24, 39, 68, 0.22)',
        globalNavigationWidth: '64px',
    },
    images: {
        logo: {
            // plugin: 'pcweb/gui',
            path: 'images/VIER_Logo.svg',
        },
    },
    buttons: {
        layout: 'theme-button-layout--responsive',
    },
    palette: {
        type: 'dark',
        primary: {
            50: '#0C1627',
            100: '#1C3257',
            200: '#3E6FC1',
            300: '#8BA9DA',
            500: '#B2C5E6',
            600: '#32599A',
            800: '#D8E2F3',
            900: '#ECF1F9',
            contrastDefaultColor: 'light',
            light: '#8BA9DA',
            main: '#B2C5E6',
            dark: '#D8E2F3',
            contrastText: '#FFF',
        },
        secondary: {
            300: '#FF8A66',
            500: '#FF3C00',
            600: '#CC3000',
            contrastDefaultColor: 'light',
            light: '#FF8A66',
            main: '#FF3C00',
            dark: '#CC3000',
            contrastText: '#FFF',
        },
        neutral: {
            /* had to change neutral 50 to #353131,
             * because the provided values don't look good  */
            50: '#353131',
            100: '#353131',
            200: '#4F4A4A',
            300: '#6A6262',
            400: '#847B7B',
            500: '#9D9595',
            600: '#B5B0B0',
            700: '#CECACA',
            800: '#E6E5E5',
            900: '#F2F1F1',
            contrastDefaultColor: 'light',
            light: '#6A6262',
            main: '#353131',
            dark: '#CECACA',
            contrastText: '#FFF',
        },
        blue: {
            50: '#ECF1F9',
            100: '#D8E2F3',
            200: '#B2C5E6',
            300: '#8BA9DA',
            500: '#3E6FC1',
            800: '#1C3257',
            900: '#0C1627',
            light: '#8BA9DA',
            main: '#3E6FC1',
            dark: '#1C3257',
        },
        status: {
            free: {
                light: '#7fed7d',
                main: '#49BA4E',
                dark: '#00891f',
            },
            refinish: {
                light: '#ffee4d',
                main: '#F5BC00',
                dark: '#bd8c00',
            },
            reserved: {
                light: '#ffa44a',
                main: '#F57316',
                dark: '#bb4400',
            },
            busy: {
                light: '#ff563d',
                main: '#D91111',
                dark: '#9f0000',
            },
            busyTapi: {
                light: '#ff563d',
                main: '#D91111',
                dark: '#9f0000',
            },
            break: {
                light: '#80d5ff',
                main: '#42A4F5',
                dark: '#0076c2',
            },
            logout: {
                light: '#C0CACF',
                main: '#484F52',
                dark: '#2D3133',
            },
        },
        background: {
            default: 'rgb(30, 30, 30)',
            paper: 'rgb(30, 30, 30)',
        },
        text: {
            primary: '#FFFFFF',
            secondary: '#544d54',
            tertiary: '#858085',
            disabled: '#B6B3B6',
        },
        error: {
            light: '#ff563d',
            main: '#D91111',
            dark: '#9f0000',
        },
        success: {
            light: '#7fed7d',
            main: '#49BA4E',
            dark: '#00891f',
            contrastText: '#FFFFFF',
        },
        info: {
            light: '#80d5ff',
            main: '#42A4F5',
            dark: '#0076c2',
        },
        action: {
            green: {
                50: '#C2EBC2',
                300: '#8DD98C',
                light: '#7fed7d',
                main: '#49BA4E',
                dark: '#00891f',
            },
            yellow: {
                50: '#FDFCEC',
                300: '#F3E872',
                light: '#ffee4d',
                main: '#F5BC00',
                dark: '#bd8c00',
            },
            orange: {
                50: '#FFFFFF',
                300: '#FF8A66',
                light: '#ffa44a',
                main: '#F57316',
                dark: '#bb4400',
            },
            red: {
                50: '#F4CDCD',
                300: '#E48181',
                light: '#ff563d',
                main: '#D91111',
                dark: '#9f0000',
            },
            pink: {
                50: '#FFFFFF',
                300: '#FFFFFF',
                light: '#ff7beb',
                main: '#F542B9',
                dark: '#BE0089',
            },
            violet: {
                50: '#FFFFFF',
                300: '#FFFFFF',
                light: '#D66EFF',
                main: '#A13BD9',
                dark: '#6D00A6',
            },
            darkBlue: {
                50: '#FFFFFF',
                300: '#FFFFFF',
                light: '#7373F5',
                main: '#4242F5',
                dark: '#0017C1',
            },
            blue: {
                50: '#E9F3FC',
                300: '#79B8EC',
                light: '#80d5ff',
                main: '#42A4F5',
                dark: '#0076c2',
            },
            teal: {
                50: '#FFFFFF',
                300: '#FFFFFF',
                light: '#84ffff',
                main: '#42E9F5',
                dark: '#00b6c2',
            },
            neutralv5: {
                50: '#F0ECEE',
            },
            hover: '#eef0f2',
            selected: '#e2e5e8',
        },
        divider: '#d4d8dd',
        utility: {
            green: {
                50: '#C2EBC2',
                light: '#7fed7d',
                main: '#49BA4E',
                dark: '#00891f',
            },
            yellow: {
                50: '#FDFCEC',
                light: '#ffee4d',
                main: '#F5BC00',
                dark: '#bd8c00',
            },
            orange: {
                50: '#FFFFFF',
                light: '#ffa44a',
                main: '#F57316',
                dark: '#bb4400',
            },
            red: {
                50: '#F4CDCD',
                light: '#ff563d',
                main: '#D91111',
                dark: '#9f0000',
            },
            pink: {
                50: '#FFFFFF',
                light: '#ff7beb',
                main: '#F542B9',
                dark: '#BE0089',
            },
            violet: {
                50: '#FFFFFF',
                light: '#D66EFF',
                main: '#A13BD9',
                dark: '#6D00A6',
            },
            darkBlue: {
                50: '#FFFFFF',
                light: '#7373F5',
                main: '#4242F5',
                dark: '#0017C1',
            },
            blue: {
                50: '#E9F3FC',
                light: '#80d5ff',
                main: '#42A4F5',
                dark: '#0076c2',
            },
            teal: {
                50: '#FFFFFF',
                light: '#84ffff',
                main: '#42E9F5',
                dark: '#00b6c2',
            },
            neutralv5: {
                50: '#F0ECEE',
            },
        },
    },
});
export default customDarkTheme;
